// ===============================================================================
// api.js - API Client for WOW Frontend
// ===============================================================================
// All API calls to the backend
// UPDATED: Added shares management and expense detail APIs
// ===============================================================================
import axios from 'axios';

// Create axios instance - point directly to backend
const api = axios.create({
    baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://195.201.29.250:8001/api',

  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});
// ===============================================================================
// RESPONSE INTERCEPTOR - Handle auth errors globally
// ===============================================================================
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      // Don't redirect if already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session_expired=true';
      }
    }
    
    // Handle 423 Account Locked
    if (error.response?.status === 423) {
      console.warn('Account locked:', error.response.data?.detail);
    }
    
    // Handle 429 Rate Limited
    if (error.response?.status === 429) {
      console.warn('Rate limited:', error.response.data?.detail);
    }
    
    return Promise.reject(error);
  }
);

// ===============================================================================
// AUTH API
// ===============================================================================

export const authAPI = {

  // OTP REGISTRATION (Two-Step)

         // Step 1: Send OTP to email
registerSendOTP: (email, username, password, name, captchaToken = null) => 
  api.post('/auth/register/send-otp', { 
    email, 
    username, 
    password, 
    name, 
    captcha_token: captchaToken 
  }),

        // Step 2: Verify OTP and create account
registerVerifyOTP: (email, otpCode) => 
  api.post('/auth/register/verify-otp', { 
    email, 
    otp_code: otpCode 
  }),

       // Resend OTP code
resendOTP: (email, captchaToken = null) => 
  api.post('/auth/register/resend-otp', { 
    email, 
    captcha_token: captchaToken 
  }),

// end of OTO registration 

    // Login with username/email and password (added captchaToken parameter)
  login: (username, password, captchaToken = null) => 
    api.post('/auth/login', { username, password, captcha_token: captchaToken }),
 
  logout: () => 
    api.post('/auth/logout'),
  
  getMe: () => 
    api.get('/auth/me'),
  
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { 
      current_password: currentPassword, 
      new_password: newPassword 
    }),
  
  updateProfile: (data) => 
    api.put('/auth/profile', data),

       // PASSWORD RESET (NEW)

  forgotPassword: (email, captchaToken = null) => 
    api.post('/auth/forgot-password', { email, captcha_token: captchaToken }),
  
  resetPassword: (token, newPassword) => 
    api.post('/auth/reset-password', { token, new_password: newPassword }),

      // SECURITY (NEW)

  getPasswordRequirements: () => 
    api.get('/auth/password-requirements'),
  
  getSecurityStatus: () => 
    api.get('/auth/security-status'),
};

// ===============================================================================
// GROUPS API
// ===============================================================================

export const groupsAPI = {
  list: () => 
    api.get('/groups'),
  
  create: (name, description = '', currency = 'EUR') => 
    api.post('/groups', { name, description, currency }),
  
  get: (groupId) => 
    api.get(`/groups/${groupId}`),
  
  update: (groupId, data) => 
    api.put(`/groups/${groupId}`, data),
  
  delete: (groupId) => 
    api.delete(`/groups/${groupId}`),
  
  join: (inviteCode) => 
    api.post('/groups/join', { invite_code: inviteCode }),
  
  leave: (groupId) => 
    api.post(`/groups/${groupId}/leave`),
  
  refreshInvite: (groupId) => 
    api.post(`/groups/${groupId}/refresh-invite`),
  
  // Split percentages
  getSplitPercentages: (groupId) =>
    api.get(`/groups/${groupId}/split-percentages`),
  
  updateSplitPercentages: (groupId, percentages) =>
    api.put(`/groups/${groupId}/split-percentages`, { percentages }),
  
  resetSplitPercentages: (groupId) =>
    api.delete(`/groups/${groupId}/split-percentages`),
  
  // Default shares (NEW)
  getDefaultShares: (groupId) =>
    api.get(`/groups/${groupId}/default-shares`),
  
  updateDefaultShares: (groupId, shares) =>
    api.put(`/groups/${groupId}/default-shares`, { shares }),
  
  resetDefaultShares: (groupId) =>
    api.delete(`/groups/${groupId}/default-shares`),
};

// ===============================================================================
// EXPENSES API
// ===============================================================================

export const expensesAPI = {
  list: (groupId, limit = 20, offset = 0) => 
    api.get(`/groups/${groupId}/expenses`, { params: { limit, offset } }),
  
  create: (groupId, data) => 
    api.post(`/groups/${groupId}/expenses`, data),
  
  get: (expenseId) => 
    api.get(`/expenses/${expenseId}`),
  
  update: (expenseId, data) => 
    api.put(`/expenses/${expenseId}`, data),
  
  delete: (expenseId) => 
    api.delete(`/expenses/${expenseId}`),
};

// ===============================================================================
// BALANCES API
// ===============================================================================

export const balancesAPI = {
  get: (groupId) => 
    api.get(`/groups/${groupId}/balances`),
};

// ===============================================================================
// SETTLEMENTS API
// ===============================================================================

export const settlementsAPI = {
  list: (groupId, limit = 20, offset = 0) => 
    api.get(`/groups/${groupId}/settlements`, { params: { limit, offset } }),
  
  create: (groupId, toUser, amount, note = '') => 
    api.post(`/groups/${groupId}/settlements`, { 
      to_user: toUser, 
      amount, 
      note 
    }),
};

// ===============================================================================
// EXPORT API
// ===============================================================================

export const exportAPI = {
  expensesCsv: (groupId) => 
    api.get(`/groups/${groupId}/export/expenses`, { responseType: 'blob' }),
  
  settlementsCsv: (groupId) => 
    api.get(`/groups/${groupId}/export/settlements`, { responseType: 'blob' }),
};

// ===============================================================================
// RECEIPTS API (Premium Feature)
// ===============================================================================

export const receiptsAPI = {
  getInfo: (expenseId) =>
    api.get(`/expenses/${expenseId}/receipt`),
  
  upload: (expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/expenses/${expenseId}/receipt`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  download: (expenseId) =>
    api.get(`/expenses/${expenseId}/receipt/download`, { responseType: 'blob' }),
  
  delete: (expenseId) =>
    api.delete(`/expenses/${expenseId}/receipt`),
};

// ===============================================================================
// UTILITY FUNCTIONS
// ===============================================================================

export const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ===============================================================================
// SEARCH API (Premium Feature)
// ===============================================================================

export const searchAPI = {
  getFilters: () =>
    api.get('/search/filters'),
  
  search: (params) =>
    api.post('/search/expenses', params),
};

// ===============================================================================
// ANALYSIS API (Premium Feature)
// ===============================================================================

export const analysisAPI = {
  getSummary: (filters) =>
    api.post('/analysis/summary', filters),
  
  getPerPerson: (filters) =>
    api.post('/analysis/per-person', filters),
  
  getTrends: (filters) =>
    api.post('/analysis/trends', filters),
  
  getInsights: (filters) =>
    api.post('/analysis/insights', filters),
};

// ===============================================================================
// MEMBERSHIP API
// ===============================================================================

export const membershipAPI = {
  getPlans: () =>
    api.get('/membership/plans'),
  
  getStatus: () =>
    api.get('/membership/status'),
  
  upgrade: (data) =>
    api.post('/membership/upgrade', data),
  
  getHistory: (limit = 20, offset = 0) =>
    api.get('/membership/history', { params: { limit, offset } }),
};

// ===============================================================================
// BILLING API (Invoice & History)
// ===============================================================================

export const billingAPI = {
  // Get billing history
  getHistory: (limit = 20, offset = 0) =>
    api.get('/billing/history', { params: { limit, offset } }),
  
  // Get invoice data
  getInvoice: (upgradeId) =>
    api.get(`/billing/invoice/${upgradeId}`),
  
  // Download invoice PDF
  downloadInvoicePdf: (upgradeId) =>
    api.get(`/billing/invoice/${upgradeId}/pdf`, { responseType: 'blob' }),
};

// ===============================================================================
// BUSINESS ANALYSIS API (Premium Feature)
// ===============================================================================

export const businessAnalysisAPI = {
  // Get available groups for filtering
  getGroups: () =>
    api.get('/business/analysis/groups'),
  
  // Summary - Group → Category → Member breakdown
  getSummary: (filters) =>
    api.post('/business/analysis/summary', filters),
  
  // Per Person - Total expenses per person
  getPerPerson: (filters) =>
    api.post('/business/analysis/per-person', filters),
  
  // Trends - Monthly spending and category breakdown
  getTrends: (filters) =>
    api.post('/business/analysis/trends', filters),
  
  // Insights - Statistics and top expenses
  getInsights: (filters) =>
    api.post('/business/analysis/insights', filters),
    // NEW: Profit/Loss - Income vs Expenses analysis per group
  getProfitLoss: (filters) =>
    api.post('/business/analysis/profit-loss', filters),
};

// ===============================================================================
// BUSINESS GROUPS API (Premium Feature)
// ===============================================================================

export const businessAPI = {
  // Business Types & Categories
  getTypes: () =>
    api.get('/business/types'),
  
  getCategoriesForType: (typeId) =>
    api.get(`/business/types/${typeId}/categories`),
  
  // Group Limit Status
  getLimit: () =>
    api.get('/business/limit'),
  
  // Groups CRUD
  listGroups: () =>
    api.get('/business/groups'),
  
  createGroup: (data) =>
    api.post('/business/groups', data),
  
  getGroup: (groupId) =>
    api.get(`/business/groups/${groupId}`),
  
  updateGroup: (groupId, data) =>
    api.put(`/business/groups/${groupId}`, data),
  
  deleteGroup: (groupId) =>
    api.delete(`/business/groups/${groupId}`),
  
  // Join/Leave
  joinGroup: (inviteCode) =>
    api.post('/business/groups/join', { invite_code: inviteCode }),
  
  leaveGroup: (groupId) =>
    api.post(`/business/groups/${groupId}/leave`),
  
  refreshInvite: (groupId) =>
    api.post(`/business/groups/${groupId}/refresh-invite`),

  // Kick Member (Admin only) - NEW
  kickMember: (groupId, userId) =>
    api.delete(`/business/groups/${groupId}/members/${userId}`),
  
  // Custom Categories
  createCustomCategory: (groupId, data) =>
    api.post(`/business/groups/${groupId}/categories`, data),
  
  deleteCustomCategory: (groupId, categoryId) =>
    api.delete(`/business/groups/${groupId}/categories/${categoryId}`),
  
  // Expenses
  getExpenses: (groupId, limit = 20, offset = 0) =>
    api.get(`/business/groups/${groupId}/expenses`, { params: { limit, offset } }),
  
  createExpense: (groupId, data) =>
    api.post(`/business/groups/${groupId}/expenses`, data),
  
  getExpense: (expenseId) =>
    api.get(`/business/expenses/${expenseId}`),
  
  updateExpense: (expenseId, data) =>
    api.put(`/business/expenses/${expenseId}`, data),
  
  deleteExpense: (expenseId) =>
    api.delete(`/business/expenses/${expenseId}`),
  
  // Balances
  getBalances: (groupId) =>
    api.get(`/business/groups/${groupId}/balances`),
  
  // Settlements
  getSettlements: (groupId, limit = 20, offset = 0) =>
    api.get(`/business/groups/${groupId}/settlements`, { params: { limit, offset } }),
  
  createSettlement: (groupId, data) =>
    api.post(`/business/groups/${groupId}/settlements`, data),
  
  // Split Percentages
  getSplitPercentages: (groupId) =>
    api.get(`/business/groups/${groupId}/split-percentages`),
  
  updateSplitPercentages: (groupId, percentages) =>
    api.put(`/business/groups/${groupId}/split-percentages`, { percentages }),
  
  resetSplitPercentages: (groupId) =>
    api.delete(`/business/groups/${groupId}/split-percentages`),
  
  // Default Shares
  getDefaultShares: (groupId) =>
    api.get(`/business/groups/${groupId}/default-shares`),
  
  updateDefaultShares: (groupId, shares) =>
    api.put(`/business/groups/${groupId}/default-shares`, { shares }),
  
  resetDefaultShares: (groupId) =>
    api.delete(`/business/groups/${groupId}/default-shares`),

// Exports
  exportExpensesCsv: (groupId) =>
    api.get(`/business/groups/${groupId}/export/expenses`, { responseType: 'blob' }),
  
  exportSettlementsCsv: (groupId) =>
    api.get(`/business/groups/${groupId}/export/settlements`, { responseType: 'blob' }),

 exportIncomeCsv: (groupId) =>
    api.get(`/business/groups/${groupId}/export/income`, { responseType: 'blob' }),

 // =========================================================================
  // RECEIPTS API - NEW
  // =========================================================================
  getReceiptInfo: (expenseId) =>
    api.get(`/business/expenses/${expenseId}/receipt`),
  
  uploadReceipt: (expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/business/expenses/${expenseId}/receipt`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  downloadReceipt: (expenseId) =>
    api.get(`/business/expenses/${expenseId}/receipt/download`, { responseType: 'blob' }),
  
  deleteReceipt: (expenseId) =>
    api.delete(`/business/expenses/${expenseId}/receipt`),

  // INCOME API - Group Income Feature
  // =========================================================================
  
  // Get income source types
  getIncomeSourceTypes: () =>
    api.get('/business/income/source-types'),
  
  // List income entries for a group
  getIncome: (groupId, limit = 20, offset = 0) =>
    api.get(`/business/groups/${groupId}/income`, { params: { limit, offset } }),
  
  // Create income entry
  createIncome: (groupId, data) =>
    api.post(`/business/groups/${groupId}/income`, data),
  
  // Get income detail
  getIncomeDetail: (incomeId) =>
    api.get(`/business/income/${incomeId}`),
  
  // Get income summary
  getIncomeSummary: (groupId) =>
    api.get(`/business/groups/${groupId}/income/summary`),
  
  // Get balances with income (includes Group Income as virtual member)
  getBalancesWithIncome: (groupId) =>
    api.get(`/business/groups/${groupId}/balances-with-income`),
  
  // Create settlement FROM Group Income
  createSettlementFromIncome: (groupId, data) =>
    api.post(`/business/groups/${groupId}/settlements/from-income`, data),
  
  // Income receipt operations
  getIncomeReceiptInfo: (incomeId) =>
    api.get(`/business/income/${incomeId}/receipt`),
  
  uploadIncomeReceipt: (incomeId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/business/income/${incomeId}/receipt`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  downloadIncomeReceipt: (incomeId) =>
    api.get(`/business/income/${incomeId}/receipt/download`, { responseType: 'blob' }),
  
  deleteIncomeReceipt: (incomeId) =>
    api.delete(`/business/income/${incomeId}/receipt`),
};
// User Setting! change name, email, delet account ..etc

export const settingsAPI = {
  // =========================================================================
  // CHANGE NAME
  // =========================================================================
  changeName: (name) =>
    api.put('/settings/name', { name }),

  // =========================================================================
  // CHANGE EMAIL (Two-Step OTP)
  // =========================================================================
  
  // Step 1: Request OTP (verify password, send OTP to new email)
  changeEmailRequest: (newEmail, password) =>
    api.post('/settings/email/request', { 
      new_email: newEmail, 
      password 
    }),
  
  // Step 2: Verify OTP and complete email change
  changeEmailVerify: (newEmail, otpCode) =>
    api.post('/settings/email/verify', { 
      new_email: newEmail, 
      otp_code: otpCode 
    }),

  // =========================================================================
  // SECURITY STATUS
  // =========================================================================
  getSecurityStatus: () =>
    api.get('/settings/security-status'),

  // =========================================================================
  // DELETE ACCOUNT
  // =========================================================================
  
  // Pre-check: Get balances, admin groups, etc.
  deleteAccountCheck: () =>
    api.get('/settings/delete-account/check'),
  
  // Transfer admin of a group
  transferAdmin: (groupId, groupType, newAdminId) =>
    api.post('/settings/delete-account/transfer-admin', {
      group_id: groupId,
      group_type: groupType,
      new_admin_id: newAdminId
    }),
  
  // Step 1: Initiate deletion (verify password, send OTP)
  deleteAccountInitiate: (password) =>
    api.post('/settings/delete-account/initiate', { password }),
  
  // Step 2: Confirm deletion (verify OTP, soft delete)
  deleteAccountConfirm: (otpCode) =>
    api.post('/settings/delete-account/confirm', { otp_code: otpCode }),

  // =========================================================================
  // ACCOUNT RECOVERY (for users with pending deletion)
  // =========================================================================
  
  // Send recovery OTP
  sendRecoveryOTP: (email) =>
    api.post('/settings/recover/send-otp', null, { params: { email } }),
  
  // Verify recovery OTP and restore account
  verifyRecoveryOTP: (email, otpCode) =>
    api.post('/settings/recover/verify', { otp_code: otpCode }, { params: { email } }),
};

// ===============================================================================
// PASSWORD VALIDATION HELPER
// ===============================================================================

export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 8) {
    errors.push('At least 8 characters');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('One uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('One lowercase letter');
  }
  if (!/\d/.test(password)) {
    errors.push('One number');
  }
  if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
    errors.push('One special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export default api;

